import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import InputAdornment from '@mui/material/InputAdornment'
import { FormControl } from '@mui/material'

import SearchIcon from '@mui/icons-material/Search'

import axios from '../../dataClient/dataClient'
import { billTypeMap } from '../../util/billingConsts'
import { parseDateOnly } from '../../util/foiFormatting'
import { CenteredContent, FlexColumn, Header, LightBGContainer, PageWithTitle, SpaceBetweenRowContainer } from '../styles/Containers'
import { formatTaskTitle } from '../tasks/TaskName'
import { PageTitle, BoldBodyText } from '../styles/Text'
import { SelectMenuItem, TextField } from '../styles/TextField'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import TaskList from '../tasks/TaskList'

const FieldWrapper = styled(FormControl)`
  width: 225px;
`

const FlexContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`

const TASK_STATUS_OPTIONS = [
  { value: 'DRAFTING', label: 'Draft' },
  { value: 'AWAITING_APPROVAL', label: 'Review' },
  { value: 'READY_TO_SEND', label: 'Send' }
]

const TASK_TYPE_OPTIONS = [
  { value: 'LETTER', label: 'Letter' },
  { value: 'BILL', label: 'Bill' }
]

function TasksPage() {
  const [tasks, setTasks] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState()
  const [currentUserId, setCurrentUserId] = useState()
  const [loadingUser, setLoadingUser] = useState(true)
  const [userError, setUserError] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterBy, setFilterBy] = useState('none')
  const [filterValue, setFilterValue] = useState('')

  function getUser() {
    axios
      .get('/user')
      .then(({ data }) => setCurrentUserId(data.data.id))
      .catch(e => {
        console.error('Failed to retrieve current user', e)
        setUserError(true)
      })
      .finally(() => setLoadingUser(false))
  }

  useEffect(() => {
    getUser()

    axios
      .get('/tasks')
      .then(({ data }) => {
        setTasks(
          data.data.flatMap(foi =>
            (foi.tasks || []).map(task => ({
              ...task,
              foi: {
                id: foi.foiId,
                secondaryId: foi.foiSecondaryId,
                requestDescription: foi.requestDescription,
                dueDate: foi.dueDate ? parseDateOnly(foi.dueDate) : null
              }
            }))
          )
        )
        setIsLoading(false)
      })
      .catch(e => {
        console.error('Failed to retrieve tasks', e)
        setError(true)
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(searchValue), 500)
    return () => clearTimeout(timeoutId)
  }, [searchValue])

  const assigneeOptions = [{ value: 'nobody', label: 'Nobody' }]
  const seenAssignees = new Set()
  for (const task of tasks) {
    if (task.assignedTo?.id && !seenAssignees.has(task.assignedTo.id)) {
      seenAssignees.add(task.assignedTo.id)
      assigneeOptions.push({ value: task.assignedTo.id, label: `${task.assignedTo.firstName} ${task.assignedTo.lastName}` })
    }
  }

  const filterByOptions = {
    assignedTo: { value: 'assignedTo', label: 'Assigned To', options: assigneeOptions },
    taskType: { value: 'taskType', label: 'Task Type', options: TASK_TYPE_OPTIONS },
    taskStatus: { value: 'taskStatus', label: 'Task Status', options: TASK_STATUS_OPTIONS }
  }

  let visibleTasks = tasks
  if (filterBy === 'taskType') visibleTasks = tasks.filter(task => task.type === filterValue)
  if (filterBy === 'taskStatus') {
    visibleTasks = tasks.filter(task => (task.type === 'LETTER' ? task.letter?.status : task.bill?.status) === filterValue)
  }
  if (filterBy === 'assignedTo') {
    visibleTasks = filterValue === 'nobody' ? tasks.filter(task => !task.assignedTo) : tasks.filter(task => task.assignedTo?.id === filterValue)
  }

  const searchVal = debouncedSearch.trim().toLowerCase()
  if (searchVal) {
    visibleTasks = visibleTasks.filter(task =>
      [
        task.foi?.secondaryId,
        task.foi?.requestDescription,
        task.letter?.name,
        task.bill?.secondaryId,
        billTypeMap[task.bill?.billType],
        formatTaskTitle(task)
      ].some(field => (field || '').toLowerCase().includes(searchVal))
    )
  }

  function onFilterByChange(event) {
    const nextFilter = event.target.value
    setFilterBy(nextFilter)
    if (nextFilter === 'none') {
      setFilterValue('')
      return
    }
    setFilterValue(filterByOptions[nextFilter].options[0].value)
  }

  if (isLoading || loadingUser) return <Loading />

  if (error || userError)
    return <LoadingError message={["We're sorry.", 'There was an error loading your tasks.', 'Please refresh the page to try again.']} />

  return (
    <PageWithTitle gap="15px">
      <Header>
        <PageTitle>Tasks</PageTitle>
      </Header>
      <CenteredContent>
        <FlexColumn gap="15px" width="100%">
          <LightBGContainer padding="25px 30px">
            <SpaceBetweenRowContainer>
              <FlexContainer>
                <BoldBodyText id="filter-type-label">FILTER BY</BoldBodyText>
                <FieldWrapper>
                  <TextField select aria-labelledby="filter-type-label" value={filterBy} onChange={onFilterByChange}>
                    <SelectMenuItem value="none">None</SelectMenuItem>
                    {Object.values(filterByOptions).map(({ value, label }) => (
                      <SelectMenuItem key={value} value={value}>
                        {label}
                      </SelectMenuItem>
                    ))}
                  </TextField>
                </FieldWrapper>
                {filterBy !== 'none' && (
                  <FieldWrapper>
                    <TextField aria-label="Filter by value" select value={filterValue} onChange={event => setFilterValue(event.target.value)}>
                      {filterByOptions[filterBy].options.map(({ value, label }) => (
                        <SelectMenuItem key={value} value={value}>
                          {label}
                        </SelectMenuItem>
                      ))}
                    </TextField>
                  </FieldWrapper>
                )}
              </FlexContainer>
              <TextField
                aria-label="Search"
                placeholder="Search..."
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </SpaceBetweenRowContainer>
          </LightBGContainer>
          <LightBGContainer padding="30px 50px 40px">
            <TaskList tasks={visibleTasks} currentUserId={currentUserId} />
          </LightBGContainer>
        </FlexColumn>
      </CenteredContent>
    </PageWithTitle>
  )
}

export default TasksPage
